import { oc } from "@orpc/contract";
import { z } from "zod";
import { ContentConflict, NoteRevision } from "./history";
import { EntityId } from "./entities";
import { apiErrors } from "./errors";
import { PullRequest, PullResponse, PushRequest, PushResponse } from "./sync";

const base = oc.errors(apiErrors);

export const DictationToken = z.object({
  token: z.string().min(1),
  /** Seconds until the token stops opening new connections. */
  expiresIn: z.number().int().positive(),
});

export const HealthResponse = z.object({
  ok: z.literal(true),
  buildSha: z.string(),
  now: z.string(),
});

/**
 * The typed application API. `health` is unauthenticated; everything else requires a
 * session, enforced by one middleware on the server.
 */
export const contract = {
  health: base.output(HealthResponse),
  notes: {
    revisions: base
      .input(
        z.object({
          id: EntityId,
          before: z.number().int().positive().optional(),
          limit: z.number().int().positive().max(100).default(30),
        }),
      )
      .output(z.array(NoteRevision)),
  },
  conflicts: {
    list: base
      .input(
        z.object({
          after: z.string().optional(),
          limit: z.number().int().positive().max(100).default(50),
        }),
      )
      .output(z.array(ContentConflict)),
    resolve: base
      .input(
        z.object({
          id: z.string(),
          resolution: z.enum(["keep_server", "keep_mine", "keep_both"]),
          baseRevision: z.number().int().nonnegative(),
        }),
      )
      .output(z.object({ resolved: z.boolean(), copyId: EntityId.optional() })),
  },
  sync: {
    push: base.input(PushRequest).output(PushResponse),
    pull: base.input(PullRequest).output(PullResponse),
  },
  dictation: {
    /** A short-lived Deepgram access token; the provider key never leaves the Worker. */
    token: base.output(DictationToken),
  },
};

export type Contract = typeof contract;
